import { Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Dispatch, SetStateAction } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';

import { useDrawer } from '@/@mantis/contexts/DrawerContext';
import { LinkTarget, NavItemType } from '@/@mantis/types/menu';

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
  setSelectedID?: Dispatch<SetStateAction<string | undefined>>;
}

const NavItem: React.FC<Props> = ({ item, level, isParents = false, setSelectedID }) => {
  const theme = useTheme();

  const { isOpen, setIsOpen } = useDrawer();

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = () => {
    if (downLG) setIsOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(undefined);
    }
  };

  const location = useLocation();
  const isSelected =
    item.getIsSelected?.(location.pathname) ??
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    !!matchPath(location.pathname, (item.link ?? item.url)!);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const Icon = item.icon!;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: '1.5rem',
        color: isSelected ? theme.palette.secondary.darker : theme.palette.secondary[600],
      }}
    />
  ) : (
    false
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListItemButton
        id={item.id?.toString()}
        component={Link}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        to={item.url!}
        target={itemTarget}
        disabled={item.disabled}
        selected={isSelected}
        sx={{
          zIndex: 1201,
          pl: isOpen ? 2 : 1.5,
          ml: isOpen ? `${(level - 1) * 28}px` : 0,
          py: !isOpen && level === 1 ? 0.75 : 0.5,
          borderRadius: '6px',
          ...(isOpen && {
            '&:hover': {
              bgcolor: theme.palette.secondary[200], // hover color
            },
            '&.Mui-selected': {
              bgcolor: theme.palette.secondary[200], // hover color
              '&:hover': {
                bgcolor: theme.palette.secondary[200], // hover color
              },
            },
          }),
          ...(!isOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' },
          }),
        }}
        onClick={() => itemHandler()}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              ...(isOpen
                ? {
                  width: 28,
                  height: 20,
                  '& svg': {
                    height: 20,
                    width: 20,
                  },
                }
                : {
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1.5,
                }),
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(isOpen || (!isOpen && level !== 1)) &&
          (level === 1 ? (
            <ListItemText
              primary={
                <Typography
                  component="span"
                  variant="body3"
                  color={theme.palette.secondary.darker} // text color for side bar
                  sx={{
                    fontFamily: 'Nohemi',
                  }}
                >
                  {item.title}
                </Typography>
              }
            />
          ) : (
            <ListItemText
              secondary={
                <Typography component="span" variant="body3" color={theme.palette.secondary.darker}>
                  {item.title}
                </Typography>
              }
            />
          ))}
        {(isOpen || (!isOpen && level !== 1)) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>
    </Box>
  );
};

export default NavItem;
